import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { usePrefersReducedMotion } from '../../../music24/promote/themes/usePrefersReducedMotion';
import { authV2Messages } from './authV2Messages';

/*
 * The welcome loop of LaziiBee (also the welcome page, onboarding Task 11): the four Neon feed posts
 * of the Forests kit (Announce, Pre-save, Out now, Lyric), rendered on production on 2026-10-09.
 * 1080 x 1350 (4:5, twice the 560 px slot), a muted 8 s H.264 loop that ends on its first frame.
 * The poster is that first frame; the still for reduced motion is the "Out now" post.
 */
const LOOP_BASE = `${process.env.PUBLIC_URL ?? ''}/statics/onboarding/laziibee-forests-neon-feed`;
export const AUTH_LOOP_VIDEO_URL = `${LOOP_BASE}-loop.mp4`;
export const AUTH_LOOP_POSTER_URL = `${LOOP_BASE}-announce.webp`;
export const AUTH_STILL_URL = `${LOOP_BASE}-out-now.webp`;
export const AUTH_STILL_SRC_SET = `${LOOP_BASE}-out-now-540.webp 540w, ${AUTH_STILL_URL} 1080w`;
/** The rendered width of the slot: 560 px at most, the full width of a phone below 720 px. */
export const AUTH_STILL_SIZES = '(max-width: 719px) 100vw, 560px';

/**
 * LaziiBee's campaign beside the form: the proof, not a feature list (design: onboarding g1, `.ob-loop`).
 * Under 720 px it becomes the short poster strip of g9. With reduced motion it shows the poster only.
 */
const AuthCampaignVisualComponent: React.FC = () => {
  const intl = useIntl();
  const reducedMotion = usePrefersReducedMotion();
  const alt = intl.formatMessage(authV2Messages.visualAlt);

  return (
    <aside className="au-visual" aria-label={alt}>
      <div className="au-visual-bg" style={{ backgroundImage: `url(${AUTH_LOOP_POSTER_URL})` }} />
      {reducedMotion ? (
        <img
          className="au-visual-media"
          src={AUTH_STILL_URL}
          srcSet={AUTH_STILL_SRC_SET}
          sizes={AUTH_STILL_SIZES}
          width={1080}
          height={1350}
          alt={alt}
        />
      ) : (
        <video
          className="au-visual-media"
          src={AUTH_LOOP_VIDEO_URL}
          poster={AUTH_LOOP_POSTER_URL}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={alt}
        />
      )}
      <span className="au-visual-label">{intl.formatMessage(authV2Messages.visualLabel)}</span>
      {/* design: g1 shows the plan phase as the current one. */}
      <div className="au-visual-phase" aria-hidden="true">
        <span>{intl.formatMessage(authV2Messages.visualPhaseSong)}</span>
        <span>{intl.formatMessage(authV2Messages.visualPhaseKit)}</span>
        <span className="on">{intl.formatMessage(authV2Messages.visualPhasePlan)}</span>
        <span>{intl.formatMessage(authV2Messages.visualPhaseNumbers)}</span>
      </div>
    </aside>
  );
};

export const AuthCampaignVisual = TypedMemo(AuthCampaignVisualComponent);
